// The travel agent's screen (prototype: register.liparifoods.com/agent). Codes arrive the moment
// they are issued; this is where the name gets checked against the code before booking.
// `embedded` = shown inside the back office (admins); otherwise it is Russ's whole page.
// Opens on Not booked, the agent's working list; the filter and the search live in the address (?show=&q=), so a
// reload or a shared link keeps them (Task 8a).
import React, { useEffect, useState } from "react";
import { api, newIdempotencyKey } from "../lib/api.js";
import { usePoll } from "../lib/usePoll.js";
import { PageHead, StatePill, Notice, StatStrip, FilterRow, fmtWhen } from "./admin/ui.jsx";
import { useConfirm } from "./ConfirmDialog.jsx";
import History from "./History.jsx";
import { followLink } from "../lib/router.js";

const method = (r) => (r.travelMethod === "B" ? `Flying${r.travellers[0]?.departCity ? ` from ${r.travellers[0].departCity}` : ""}` : "Driving");
const names = (r) => r.travellers.map(t => t.name).join(", ");
// Who may be booked now: anyone with a cancellation waiting is left off until the events team decides.
const bookable = (r) => r.travellers.filter(t => !t.cancelRequested).map(t => t.name);
const waiting = (r) => r.travellers.filter(t => t.cancelRequested).map(t => t.name);
const waitLine = (w) => `${w.join(", ")} ${w.length > 1 ? "have" : "has"} a cancellation waiting`;
const day = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
// The agent's last follow-up still stands only if nothing changed on the code after it: a cancellation approved, one
// declined for someone left off the booking, or a hotel name changed since booking.
const changesSince = (r, at) => [...r.removed.map(x => x.canceledAt), ...r.travellers.map(t => t.declinedAt), ...r.renamed.map(x => x.at)]
  .filter(d => d && (!at || new Date(d) > new Date(at)));
const standing = (r) => r.followUp && !changesSince(r, r.followUp.at).length ? r.followUp : null;
const PAGE = 25;
const SHOW = [["notBooked", "Not booked"], ["booked", "Booked"], ["needsAction", "Needs action"], ["waiting", "Waiting on registrant"], ["canceled", "All canceled"]];
const matches = (r, q) => [r.code, r.registrant, ...r.travellers.map(t => t.name), ...r.removed.map(x => x.name)].some(s => s?.toLowerCase().includes(q));
const followUpText = (f) => ({ released: `Room released ${day(f.at)}`, updated: `Booking updated ${day(f.at)}`,
  queried: `Asked the registrant to call ${day(f.at)}${f.note ? `: "${f.note}"` : ""}` }[f.status]);

// Declared outside Agent on purpose: defined inside, every 10-second refresh made it a new component,
// so React replaced every row and a click landing mid-refresh was lost.
function Row({ r, detail, canBook, embedded, busy, onBook, onFollowUp, onHistory }) {
  // Everyone on it was canceled: nobody may travel on this code (the API refuses to book it too).
  const empty = !r.travellers.length;
  const booked = r.agentStatus === "used";
  const f = standing(r);
  // What the agent still has to do comes first, by the morning email's rules (the API's needsAction).
  const state = r.needsAction ? `need-${r.needsAction}` : f?.status === "released" ? "released" : empty ? (booked ? "emptied-used" : "emptied") : f?.status ?? r.agentStatus;
  // Left off the booking while their cancellation waited, then declined: they're still traveling.
  const addBack = booked && f?.status !== "updated" ? r.travellers.filter(t => t.declinedAt) : [];
  // Same rule as the API: once asked, not again until someone else is canceled off the code.
  const asked = r.followUp?.status === "queried" && !r.removed.some(x => new Date(x.canceledAt) > new Date(r.followUp.at));
  // The registrant books for everyone; when they're not on this code (another room, or they canceled), show how to reach them.
  const callBack = !r.travellers.some(t => t.registrant) && r.registrantPhone ? ` (registrant, ${r.registrantPhone})` : "";
  return (
    <div className="bo-tr">
      {/* History sits with the code it belongs to, so the last column keeps only what changes a booking. */}
      {/* In the back office the code opens its invitation's record page (Task B3). */}
      <div>{embedded && r.invitationId
          ? <div><a className="mono code big" href={`/admin/record/${r.invitationId}`} onClick={followLink(`/admin/record/${r.invitationId}`)}>{r.code}</a></div>
          : <div className="mono code big">{r.code}</div>}
        <button className="bo-link history" aria-label={`History of ${r.code}`} onClick={() => onHistory(r)}>History</button></div>
      <div className="name"><b className={empty ? "bad" : undefined}>{empty ? "No one on this code. Do not book." : r.travellers.map((t, i) => (
          <React.Fragment key={t.email}>{i ? ", " : ""}{t.name}{t.cancelRequested && <span className="pending"> (cancellation requested: {booked ? "the events team is deciding" : "don't book for them yet"})</span>}
          {addBack.includes(t) && <span className="pending"> (cancellation declined {day(t.declinedAt)}: add them to the booking)</span>}</React.Fragment>))}</b>
        <div className="muted small">{empty
          ? `All travelers canceled · registered by ${r.registrant}${callBack} · ${method(r)}`
          : `${r.role === "guest" ? "Guest" : "Lipari"} · ${r.registrant}${callBack} · ${method(r)}`}</div>
        {r.removed.length > 0 && <div className="small muted">{r.removed.map((x, i) => <React.Fragment key={i}>{i ? " · " : ""}<s>{x.name}</s> canceled {day(x.canceledAt)}</React.Fragment>)}</div>}
        {r.renamed.length > 0 && <div className="small muted">{r.renamed.map((x, i) => <React.Fragment key={i}>{i ? " · " : ""}Renamed {day(x.at)}: {x.from} → {x.to}</React.Fragment>)}</div>}
        {f && <div className="small muted">{followUpText(f)}</div>}
        {detail && r.travellers.map(t => <div key={t.email} className="mono small muted">{t.name}{t.registrant ? " (registrant)" : ""} · {t.email}{t.phone ? ` · ${t.phone}` : ""}</div>)}</div>
      <div className="muted">{fmtWhen(r.issuedAt)}</div>
      <div><StatePill state={state} /></div>
      {canBook && <div className="actions">
        {!empty && !booked && bookable(r).length > 0 && <button className="bo-link" disabled={busy} onClick={() => onBook(r)}>Mark booked</button>}
        {booked && empty && f?.status !== "released" && <button className="bo-link" disabled={busy} onClick={() => onFollowUp(r, "released")}>Mark released</button>}
        {booked && !empty && (r.removed.some(x => x.afterBooking) || addBack.length > 0 || r.renamed.length > 0) && f?.status !== "updated" && <button className="bo-link" disabled={busy} onClick={() => onFollowUp(r, "updated")}>Mark updated</button>}
        {/* Only while nothing is settled: a standing ask, update or release leaves the row with nothing to do. */}
        {booked && r.removed.some(x => x.afterBooking) && !f && !asked && <button className="bo-link" disabled={busy} onClick={() => onFollowUp(r, "ask-registrant")}>Ask the registrant to call</button>}
      </div>}
    </div>
  );
}

const FOLLOW_UP_DIALOGS = {
  released: (r) => ({ title: `Mark ${r.code} released?`, body: "No one is on this code any more. Mark it once the room has been let go. This can't be undone here.", confirmLabel: "Mark released", cancelLabel: "Not yet" }),
  updated: (r) => {
    const changed = changesSince(r, r.followUp?.at);
    const off = r.removed.filter(x => x.afterBooking && changed.includes(x.canceledAt)).map(x => x.name);
    const add = r.travellers.filter(t => t.declinedAt && changed.includes(t.declinedAt)).map(t => t.name);
    const ren = r.renamed.filter(x => changed.includes(x.at)).map(x => `${x.from} is now ${x.to}`);
    const body = [add.length ? `The booking now includes ${add.join(", ")}.` : off.length || !ren.length ? `The booking is now for ${names(r)} only.` : "",
      off.length ? `${off.join(", ")} ${off.length > 1 ? "are" : "is"} off it.` : "",
      ren.length ? `${ren.join(", ")} on the booking.` : ""].filter(Boolean).join(" ");
    return { title: `Mark ${r.code} updated?`, body, confirmLabel: "Mark updated", cancelLabel: "Not yet" };
  },
  "ask-registrant": (r) => ({ title: `Ask ${r.registrant} to call about ${r.code}?`, body: "We'll email them to call you, with your note.", note: "What do you need from them? (optional)", confirmLabel: "Send the email", cancelLabel: "Go back" }),
};

export default function Agent({ canBook, embedded }) {
  const [data, setData] = useState({ counts: {}, codes: [] });
  const [q, setQ] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, ask] = useConfirm();
  const [show, setShow] = useState(() => new URLSearchParams(window.location.search).get("show") ?? "notBooked");
  const [find, setFind] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
    const p = new URLSearchParams(window.location.search);
    if (show === "notBooked") p.delete("show"); else p.set("show", show);
    if (find) p.set("q", find); else p.delete("q");
    const qs = p.toString();
    // Browsers limit how often the address can change; a burst of typing can hit it, and the next change catches up.
    try { window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`); } catch { /* see above */ }
  }, [show, find]);

  const load = async () => { try { setData(await api("/agent/codes?limit=1000")); setError(null); } catch (err) { setError(err.message); } };
  usePoll(load, 10000);

  async function lookup(e, code = q.trim().toUpperCase()) {
    e?.preventDefault();
    if (!code) return;
    setBusy(true);
    try { setCheck({ ok: true, row: await api(`/agent/codes/${encodeURIComponent(code)}`) }); }
    catch (err) { setCheck({ ok: false, error: err.message, code }); }
    finally { setBusy(false); }
  }
  async function booked(r) {
    // Booking can't be reversed from the app, so name the people it is for before it counts.
    const w = waiting(r);
    const body = w.length ? `Book for ${bookable(r).join(", ")} only. ${waitLine(w)}: don't book for them yet. This can't be undone here.`
      : `${names(r) ? `For ${names(r)}. ` : ""}This can't be undone here.`;
    if (!(await ask({ title: `Mark ${r.code} booked?`, body, confirmLabel: "Mark booked", cancelLabel: "Not yet" }))) return;
    setBusy(true);
    try { await api(`/agent/codes/${encodeURIComponent(r.code)}/booked`, { method: "POST", body: { waiting: w.length } }); await load(); if (check?.row?.code === r.code) await lookup(null, r.code); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function followUp(r, action) {
    const answer = await ask(FOLLOW_UP_DIALOGS[action](r));
    if (!answer) return;
    setBusy(true);
    try {
      const body = action === "updated" ? { seen: r.removed.length } : answer.note ? { note: answer.note } : {};
      await api(`/agent/codes/${encodeURIComponent(r.code)}/${action}`, { method: "POST", body, idempotencyKey: newIdempotencyKey() });
      await load(); if (check?.row?.code === r.code) await lookup(null, r.code);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function history(r) {
    try {
      const { events } = await api(`/agent/codes/${encodeURIComponent(r.code)}/history`);
      await ask({ title: `History of ${r.code}`, confirmLabel: "Close", cancelLabel: null,
        body: <History events={events} /> });
    } catch (err) { setError(err.message); }
  }
  const rowProps = { canBook, embedded, busy, onBook: booked, onFollowUp: followUp, onHistory: history };
  // shortcut: filters, search and pages run over the codes loaded (up to 1,000); move them to the server if the food
  // show outgrows that. The counts come from the API, so they always cover every code.
  const needle = find.trim().toLowerCase();
  const rows = data.codes.filter(r => (show === "any" || r.group === show) && (!needle || matches(r, needle)));
  const pages = Math.max(1, Math.ceil(rows.length / PAGE)), at = Math.min(page, pages - 1);
  const g = data.counts.groups ?? {};
  // The checked code follows the 10-second refresh while it's in the list.
  const checkRow = check?.ok ? (data.codes.find(c => c.code === check.row.code) ?? check.row) : null;

  const body = (
    <>
      <PageHead title="Check a code before you book" sub="Codes arrive here the moment they are issued. A code only works for the people named on it: if the caller's name is not against the code, do not book." />
      <form className="bo-bar check" onSubmit={lookup}>
        <input className="bo-code-input" value={q} onChange={e => setQ(e.target.value)} placeholder="BAM-000000" autoComplete="off" aria-label="Code" />
        <button className="bo-btn primary" type="submit" disabled={busy}>Check</button>
      </form>
      {check && (check.ok
        ? <div className="bo-table codes">{!checkRow.travellers.length
            ? <Notice tone="bad">No one is on this code any more. Do not book against it.</Notice>
            : checkRow.agentStatus === "used" || !waiting(checkRow).length ? <Notice tone="ok">Valid code. Book only for the names below.</Notice>
            : !bookable(checkRow).length ? <Notice tone="bad">Everyone on this code has a cancellation waiting. Don't book until the events team decides.</Notice>
            : <Notice tone="ok">Valid code. Book only for {bookable(checkRow).join(", ")}: {waitLine(waiting(checkRow))}.</Notice>}<Row r={checkRow} detail {...rowProps} /></div>
        : <Notice tone="bad">{check.code}: {check.error}</Notice>)}
      {error && <Notice tone="bad">{error}</Notice>}
      {dialog}
      <StatStrip items={[
        { k: "Total codes", v: data.counts.total ?? 0 },
        { k: "Not booked", v: g.notBooked ?? 0, sub: "to book" },
        { k: "Booked", v: g.booked ?? 0, sub: "nothing to do" },
        { k: "Needs action", v: g.needsAction ?? 0, sub: "release or update", color: g.needsAction ? "#8a5a00" : undefined },
      ]} />
      <div className="bo-filter-row">
        <FilterRow label="Show" value={show} onChange={setShow} options={[...SHOW.map(([value, label]) => ({ value, label, count: g[value] ?? 0 })),
          { value: "any", label: "Any", count: data.counts.total ?? 0, tone: "dark" }]} />
        <input className="bo-search" placeholder="Search code or name" aria-label="Search" value={find} onChange={e => setFind(e.target.value)} />
      </div>
      {data.codes.length < (data.counts.total ?? 0) && <Notice tone="info">Showing the newest {data.codes.length} of {data.counts.total} codes: the
        filters and search cover those. Check finds any code.</Notice>}
      <div className="bo-table codes">
        <div className="bo-th"><div>Code</div><div>Names on it</div><div>Issued</div><div>Agent</div><div /></div>
        {rows.slice(at * PAGE, at * PAGE + PAGE).map(r => <Row key={r.code} r={r} {...rowProps} />)}
        {!rows.length && <div className="bo-empty">{data.codes.length ? "No codes match." : "No codes yet."}</div>}
      </div>
      {rows.length > PAGE && <div className="bo-pager">
        <span>{at * PAGE + 1}–{Math.min(rows.length, at * PAGE + PAGE)} of {rows.length}</span>
        <button className="bo-link" disabled={at === 0} onClick={() => setPage(at - 1)}>Previous</button>
        <button className="bo-link" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>Next</button>
      </div>}
    </>
  );
  return embedded ? body : <div className="bo"><main className="bo-main agent-page">{body}</main></div>;
}
